import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import PresentationPage from './Presentation';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const PAGE_SIZE = 9;
const FAVORITES_COOKIE = 'sanatorium_favorites';
const YANDEX_MAPS_API_KEY = 'bc58bb0f-8619-4d0a-bc80-9bed1316b955';
let yandexMapsPromise;

function sanatoriumPath(id) {
  return `/sanatorium/${encodeURIComponent(id)}`;
}

function getFavoriteLinks() {
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${FAVORITES_COOKIE}=`));
  if (!cookie) return [];

  try {
    const links = JSON.parse(decodeURIComponent(cookie.slice(FAVORITES_COOKIE.length + 1)));
    if (!Array.isArray(links)) return [];
    return [...new Set(links.filter((link) => typeof link === 'string' && /^\/sanatorium\/[^/]+\/?$/.test(link)))];
  } catch {
    return [];
  }
}

function saveFavoriteLinks(links) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${FAVORITES_COOKIE}=${encodeURIComponent(JSON.stringify(links))}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

function sanatoriumIdFromPath(path) {
  const match = path.match(/^\/sanatorium\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function apiUrl(path) {
  return `${API_BASE}${path}`;
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    credentials: 'include',
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Ошибка запроса (${response.status})`);
  }
  return data;
}

function getValue(object, path, fallback = '') {
  const value = path.split('.').reduce((current, key) => current?.[key], object);
  return value ?? fallback;
}

function formatPrice(value) {
  const price = Number(value);
  if (!Number.isFinite(price) || price <= 0) return 'Цена по запросу';
  return `${new Intl.NumberFormat('ru-RU').format(price)} ₽`;
}

function formatTime(value) {
  const match = String(value || '').match(/\b(\d{1,2})[:.](\d{2})\b/);
  if (!match) return value || 'По запросу';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function procedureName(label) {
  return label.replace(/^\s*\d+(?:\.\d+)*\.\s*/, '').trim();
}

function normalizeProfileFilter(profile) {
  return profile.replace(/^\s*\(\s*\d+\s*\)\s*/, '').trim();
}

function assistantAnswer(text) {
  const content = String(text || '').trim().replace(/^```(?:json)?\s*|\s*```$/gi, '');
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed.answer === 'string') return parsed.answer;
  } catch {
    // Continue with the field-level recovery for malformed model output.
  }

  const answerField = content.match(/["']answer["']\s*:\s*([\s\S]*?)(?=\r?\n\s*["']san_name["']\s*:|$)/);
  if (answerField) {
    let answer = answerField[1].trim();
    try {
      answer = JSON.parse(answer);
    } catch {
      answer = answer.replace(/^["']/, '').trim().replace(/["',\s]+$/, '');
      if (answer.startsWith('{') && answer.endsWith('}')) {
        answer = answer.slice(1, -1).trim();
      }
      answer = answer.replace(/^[\ufeff\u200b\ufe0f\s]+|[\ufeff\u200b\ufe0f\s]+$/g, '');
    }
    if (answer) return answer;
  }
  return content;
}

function renderInlineMarkdown(text) {
  const tokens = /(\*\*.+?\*\*|__.+?__|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^ )]+\))/g;
  return text.split(tokens).filter(Boolean).map((part, index) => {
    if (part.startsWith('**') || part.startsWith('__')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`')) return <code key={index}>{part.slice(1, -1)}</code>;
    if (part.startsWith('[')) {
      const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^ )]+)\)$/);
      if (link) return <a key={index} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>;
    }
    return part;
  });
}

function MarkdownText({ children }) {
  const blocks = [];
  let paragraph = [];
  let list = [];
  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', lines: paragraph });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: 'list', lines: list });
      list = [];
    }
  };

  String(children || '').split(/\r?\n/).forEach((line) => {
    const trimmed = line.trim();
    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    const listItem = trimmed.match(/^(?:[-*+]|\d+[.)])\s+(.+)$/);
    if (!trimmed) {
      flushParagraph();
      flushList();
    } else if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', level: Math.min(heading[1].length, 4), text: heading[2] });
    } else if (listItem) {
      flushParagraph();
      list.push(listItem[1]);
    } else {
      flushList();
      paragraph.push(line);
    }
  });
  flushParagraph();
  flushList();

  return <div className="markdown-content">{blocks.map((block, index) => {
    if (block.type === 'heading') {
      const Heading = `h${block.level + 2}`;
      return <Heading key={index}>{renderInlineMarkdown(block.text)}</Heading>;
    }
    if (block.type === 'list') {
      return <ul key={index}>{block.lines.map((line, itemIndex) => <li key={itemIndex}>{renderInlineMarkdown(line)}</li>)}</ul>;
    }
    return <p key={index}>{block.lines.map((line, lineIndex) => <span key={lineIndex}>{lineIndex > 0 && <br />}{renderInlineMarkdown(line)}</span>)}</p>;
  })}</div>;
}

function Icon({ name, size = 20 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  if (name === 'search') return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>;
  if (name === 'pin') return <svg {...common}><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg>;
  if (name === 'arrow') return <svg {...common}><path d="M5 12h14m-6-6 6 6-6 6" /></svg>;
  if (name === 'back') return <svg {...common}><path d="m15 18-6-6 6-6" /></svg>;
  if (name === 'chat') return <svg {...common}><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" /></svg>;
  if (name === 'heart' || name === 'heart-filled') return <svg {...common} fill={name === 'heart-filled' ? 'currentColor' : 'none'}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" /></svg>;
  if (name === 'close') return <svg {...common}><path d="m18 6-12 12M6 6l12 12" /></svg>;
  if (name === 'send') return <svg {...common}><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>;
  if (name === 'pool') return <svg {...common}><path d="M3 16c1.5 0 1.5 1 3 1s1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1 1.5-1 3-1" /><path d="M5 13V5h6v8m0-5h7v5M8 5V3m8 5V6" /></svg>;
  if (name === 'next') return <svg {...common}><path d="m9 18 6-6-6-6" /></svg>;
  return null;
}

function loadYandexMaps() {
  if (window.ymaps) return Promise.resolve(window.ymaps);
  if (yandexMapsPromise) return yandexMapsPromise;

  yandexMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(YANDEX_MAPS_API_KEY)}&lang=ru_RU`;
    script.async = true;
    script.onload = () => {
      if (!window.ymaps) {
        reject(new Error('Не удалось инициализировать Яндекс Карты.'));
        return;
      }
      window.ymaps.ready(() => resolve(window.ymaps));
    };
    script.onerror = () => reject(new Error('Не удалось загрузить Яндекс Карты.'));
    document.head.appendChild(script);
  }).catch((error) => {
    yandexMapsPromise = null;
    throw error;
  });

  return yandexMapsPromise;
}

function getInitialRoute() {
  const match = window.location.pathname.match(/^\/sanatorium\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isPresentationPath() {
  return /^\/present\/?$/.test(window.location.pathname);
}

function App() {
  const [sanatoriumId, setSanatoriumId] = useState(getInitialRoute);
  const [isPresentation, setIsPresentation] = useState(isPresentationPath);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    profile: '',
    region: '',
    excursionTheme: '',
    procedures: [],
    pool: '',
    sort: 'price_asc',
  });
  const [procedureInput, setProcedureInput] = useState('');
  const [page, setPage] = useState(1);
  const [catalog, setCatalog] = useState({ data: [], total: 0, total_pages: 0 });
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [favoriteLinks, setFavoriteLinks] = useState(getFavoriteLinks);
  const [favoritesOpen, setFavoritesOpen] = useState(false);

  useEffect(() => {
    saveFavoriteLinks(favoriteLinks);
  }, [favoriteLinks]);

  const toggleFavorite = useCallback((id) => {
    const link = sanatoriumPath(id);
    setFavoriteLinks((current) => current.includes(link)
      ? current.filter((favorite) => favorite !== link)
      : [...current, link]);
  }, []);

  const navigate = useCallback((id) => {
    const path = id ? sanatoriumPath(id) : '/';
    window.history.pushState({}, '', path);
    setSanatoriumId(id || null);
    setIsPresentation(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setSanatoriumId(getInitialRoute());
      setIsPresentation(isPresentationPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!sanatoriumId) return undefined;
    const controller = new AbortController();
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    request(`/api/sanatoriums/${encodeURIComponent(sanatoriumId)}`, { signal: controller.signal })
      .then(setDetail)
      .catch((error) => {
        if (error.name !== 'AbortError') setDetailError(error.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [sanatoriumId]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), sort_by: filters.sort });
    if (search.trim()) params.set('q', search.trim());
    if (filters.minPrice !== '') params.set('min_price', filters.minPrice);
    if (filters.maxPrice !== '') params.set('max_price', filters.maxPrice);
    const profile = normalizeProfileFilter(filters.profile);
    if (profile) params.set('profile', profile);
    if (filters.region.trim()) params.set('region', filters.region.trim());
    if (filters.excursionTheme) params.set('excursion_theme', filters.excursionTheme);
    if (filters.pool !== '') params.set('has_pool', filters.pool);
    (Array.isArray(filters.procedures) ? filters.procedures : []).forEach((procedure) => params.append('procedure', procedure));
    return params.toString();
  }, [filters, search]);

  useEffect(() => {
    if (sanatoriumId || isPresentation) return undefined;
    const controller = new AbortController();
    setCatalogLoading(true);
    const timer = window.setTimeout(() => {
      setCatalogError('');
      request(`/api/sanatoriums?${query}&page=${page}`, { signal: controller.signal })
        .then((result) => {
          const resultItems = Array.isArray(result?.data) ? result.data : [];
          const normalizedResult = {
            ...result,
            data: resultItems,
            total: Number.isFinite(result?.total) ? result.total : 0,
            total_pages: Number.isFinite(result?.total_pages) ? result.total_pages : 0,
          };
          setCatalog((current) => {
            if (page === 1) return normalizedResult;
            const currentItems = Array.isArray(current.data) ? current.data : [];
            const existing = new Set(currentItems.map((item) => item.external_id || item._id));
            return {
              ...normalizedResult,
              data: [...currentItems, ...resultItems.filter((item) => !existing.has(item.external_id || item._id))],
            };
          });
        })
        .catch((error) => {
          if (error.name !== 'AbortError') setCatalogError(error.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setCatalogLoading(false);
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [page, query, sanatoriumId, isPresentation]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ minPrice: '', maxPrice: '', profile: '', region: '', excursionTheme: '', procedures: [], pool: '', sort: 'price_asc' });
    setProcedureInput('');
    setSearch('');
    setPage(1);
  };

  if (isPresentation) return <PresentationPage onCatalog={() => navigate(null)} />;

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="header-inner">
          <button className="brand" onClick={() => navigate(null)} aria-label="На главную">
            <span className="brand-mark">с</span>
            <span>СанаториумРФ</span>
          </button>
          <button className="header-chat" onClick={() => { setFavoritesOpen(false); setChatOpen(true); }}>
            <span className="online-dot" /> Подобрать с AI
          </button>
        </div>
      </header>

      <main>
        {sanatoriumId ? (
          <DetailPage
            data={detail}
            loading={detailLoading}
            error={detailError}
            onBack={() => navigate(null)}
            sanatoriumId={sanatoriumId}
            isFavorite={favoriteLinks.includes(sanatoriumPath(sanatoriumId))}
            onToggleFavorite={toggleFavorite}
          />
        ) : (
          <CatalogPage
            search={search}
            setSearch={(value) => { setSearch(value); setPage(1); }}
            filters={filters}
            procedureInput={procedureInput}
            setProcedureInput={setProcedureInput}
            updateFilter={updateFilter}
            resetFilters={resetFilters}
            catalog={catalog}
            loading={catalogLoading}
            error={catalogError}
            setPage={setPage}
            onSelect={navigate}
            favoriteLinks={favoriteLinks}
            onToggleFavorite={toggleFavorite}
          />
        )}
      </main>

      <footer className="site-footer">
        <span className="footer-brand">СанаториумРФ</span>
        <span>Подбор санаториев для отдыха и восстановления</span>
      </footer>

      <FavoritesWidget
        open={favoritesOpen}
        setOpen={(open) => { setFavoritesOpen(open); if (open) setChatOpen(false); }}
        links={favoriteLinks}
        onRemove={(link) => setFavoriteLinks((current) => current.filter((favorite) => favorite !== link))}
        onSelect={(id) => { setFavoritesOpen(false); navigate(id); }}
        titles={Object.fromEntries([
          ...(Array.isArray(catalog.data) ? catalog.data : []).map((item) => [
            sanatoriumPath(item.external_id || item._id),
            getValue(item, 'general_info.name', 'Санаторий'),
          ]),
          ...(detail ? [[sanatoriumPath(sanatoriumId), getValue(detail, 'general_info.name', 'Санаторий')]] : []),
        ])}
      />
      <ChatWidget
        open={chatOpen}
        setOpen={(open) => { setChatOpen(open); if (open) setFavoritesOpen(false); }}
        onSelectSanatorium={navigate}
      />
    </div>
  );
}

function CatalogPage({ search, setSearch, filters, procedureInput, setProcedureInput, updateFilter, resetFilters, catalog, loading, error, setPage, onSelect, favoriteLinks, onToggleFavorite }) {
  const [procedureSuggestions, setProcedureSuggestions] = useState([]);
  const [procedureError, setProcedureError] = useState('');
  const [filterOptions, setFilterOptions] = useState({ regions: [], excursion_themes: [] });
  const [filterOptionsError, setFilterOptionsError] = useState('');
  const [showRegionSuggestions, setShowRegionSuggestions] = useState(false);
  const [showProfileSuggestions, setShowProfileSuggestions] = useState(false);
  const procedureFieldRef = useRef(null);
  const regionFieldRef = useRef(null);
  const profileFieldRef = useRef(null);
  const selectedProcedures = Array.isArray(filters.procedures) ? filters.procedures : [];
  const suggestions = Array.isArray(procedureSuggestions) ? procedureSuggestions : [];
  const regionOptions = Array.isArray(filterOptions.regions) ? filterOptions.regions : [];
  const excursionThemeOptions = Array.isArray(filterOptions.excursion_themes) ? filterOptions.excursion_themes : [];
  const medicalProfileOptions = Array.isArray(filterOptions.medical_profiles) ? filterOptions.medical_profiles : [];
  const matchingRegions = filters.region.trim()
    ? regionOptions.filter((region) => region.toLocaleLowerCase('ru').includes(filters.region.trim().toLocaleLowerCase('ru'))).slice(0, 10)
    : [];
  const matchingProfiles = filters.profile.trim()
    ? medicalProfileOptions.filter((profile) => profile.toLocaleLowerCase('ru').includes(filters.profile.trim().toLocaleLowerCase('ru'))).slice(0, 10)
    : [];
  const catalogItems = Array.isArray(catalog?.data) ? catalog.data : [];
  const totalCount = Number.isFinite(catalog?.total) ? catalog.total : 0;
  const activeFilters = [filters.minPrice, filters.maxPrice, filters.profile, filters.region, filters.excursionTheme, filters.pool].some(Boolean) || selectedProcedures.length > 0;
  const hasMore = catalogItems.length > 0 && catalogItems.length < totalCount;

  useEffect(() => {
    const controller = new AbortController();
    request('/api/filter-options', { signal: controller.signal })
      .then((result) => setFilterOptions({
        regions: Array.isArray(result?.regions) ? result.regions : [],
        excursion_themes: Array.isArray(result?.excursion_themes) ? result.excursion_themes : [],
        medical_profiles: Array.isArray(result?.medical_profiles) ? result.medical_profiles : [],
      }))
      .catch((optionsError) => {
        if (optionsError.name !== 'AbortError') setFilterOptionsError(optionsError.message);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const text = procedureInput.trim();
    if (!text) {
      setProcedureSuggestions([]);
      setProcedureError('');
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setProcedureError('');
      request(`/api/treatment-methods?q=${encodeURIComponent(text)}`, { signal: controller.signal })
        .then((result) => setProcedureSuggestions(Array.isArray(result?.data) ? result.data : []))
        .catch((suggestionError) => {
          if (suggestionError.name !== 'AbortError') setProcedureError(suggestionError.message);
        });
    }, 180);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [procedureInput]);

  useEffect(() => {
    if (!procedureInput.trim()) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!procedureFieldRef.current?.contains(event.target)) setProcedureSuggestions([]);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [procedureInput]);

  useEffect(() => {
    if (!showRegionSuggestions) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!regionFieldRef.current?.contains(event.target)) setShowRegionSuggestions(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [showRegionSuggestions]);

  useEffect(() => {
    if (!showProfileSuggestions) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!profileFieldRef.current?.contains(event.target)) setShowProfileSuggestions(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [showProfileSuggestions]);

  const chooseProcedure = (procedure) => {
    if (!procedure || typeof procedure.label !== 'string') return;
    const name = procedureName(procedure.label);
    if (!name) return;
    if (!selectedProcedures.includes(name)) {
      updateFilter('procedures', [...selectedProcedures, name]);
    }
    setProcedureInput('');
    setProcedureSuggestions([]);
  };

  const removeProcedure = (procedure) => {
    updateFilter('procedures', selectedProcedures.filter((item) => item !== procedure));
  };

  const chooseProfile = (profile) => {
    updateFilter('profile', normalizeProfileFilter(profile));
    setShowProfileSuggestions(false);
  };

  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(loading);
  useEffect(() => {
    loadingMoreRef.current = loading;
  }, [loading]);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading || error) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMoreRef.current) {
        loadingMoreRef.current = true;
        setPage((current) => current + 1);
      }
    }, { rootMargin: '320px 0px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [error, hasMore, loading, setPage]);

  return (
    <>
      <section className="catalog-hero">
        <div className="hero-copy">
          <h1>Время позаботиться<br />о <em>себе.</em></h1>
          <p>Санатории, подобранные под ваше самочувствие,<br className="desktop-break" /> настроение и представление об отдыхе.</p>
          <div className="hero-note"><span className="note-line" /> Курорты России — от моря до гор</div>
        </div>
        <div className="hero-image" role="img" aria-label="Горный пейзаж у озера">
          <div className="image-caption"><span>Кавказские Минеральные Воды</span><span>44° 02' N&nbsp;&nbsp; 43° 05' E</span></div>
        </div>
        <div className="hero-index">01 <span>—</span> 09</div>
      </section>

      <section className="catalog-section" aria-labelledby="catalog-heading">
        <div className="section-heading">
          <div>
            <span className="eyebrow">КОЛЛЕКЦИЯ МЕСТ</span>
            <h2 id="catalog-heading">Найдите свой санаторий</h2>
          </div>
          <span className="result-count">{loading ? 'Обновляем подборку…' : `${totalCount} ${pluralize(totalCount, 'санаторий', 'санатория', 'санаториев')}`}</span>
        </div>

        <div className="search-row">
          <label className="search-box">
            <Icon name="search" size={21} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Название, регион или направление лечения"
              aria-label="Поиск санатория"
            />
            {search && <button className="clear-search" onClick={() => setSearch('')} aria-label="Очистить поиск"><Icon name="close" size={17} /></button>}
          </label>
          <label className="sort-control">
            <span>Сортировка</span>
            <select value={filters.sort} onChange={(event) => updateFilter('sort', event.target.value)} aria-label="Сортировка">
              <option value="price_asc">Сначала доступные</option>
              <option value="price_desc">Сначала дороже</option>
              <option value="procedures_desc">Больше процедур</option>
              <option value="procedures_asc">Меньше процедур</option>
            </select>
          </label>
        </div>

        <div className="filter-panel">
          <div className="filter-title"><span className="filter-icon">⌘</span><span>Уточнить подбор</span></div>
          <div className="filter-controls">
            <label className="filter-field">
              <span>Бюджет за ночь, ₽</span>
              <div className="price-range">
                <input type="number" min="0" value={filters.minPrice} onChange={(event) => updateFilter('minPrice', event.target.value)} placeholder="От" aria-label="Минимальная цена" />
                <span>—</span>
                <input type="number" min="0" value={filters.maxPrice} onChange={(event) => updateFilter('maxPrice', event.target.value)} placeholder="До" aria-label="Максимальная цена" />
              </div>
            </label>
            <div className="filter-field profile-filter" ref={profileFieldRef}>
              <label htmlFor="profile-search">Профиль лечения</label>
              <input
                id="profile-search"
                type="search"
                value={filters.profile}
                onFocus={() => setShowProfileSuggestions(true)}
                onChange={(event) => {
                  updateFilter('profile', event.target.value);
                  setShowProfileSuggestions(true);
                }}
                placeholder="Например, сердце"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showProfileSuggestions && matchingProfiles.length > 0}
                aria-controls="profile-suggestions"
              />
              {showProfileSuggestions && filters.profile.trim() && (
                <div className="procedure-suggestions" id="profile-suggestions" role="listbox">
                  {matchingProfiles.length
                    ? matchingProfiles.map((profile) => (
                      <button
                        type="button"
                        className="procedure-suggestion"
                        key={profile}
                        role="option"
                        aria-selected={filters.profile === profile}
                        onClick={() => chooseProfile(profile)}
                      >
                        <span>{profile}</span><Icon name="next" size={15} />
                      </button>
                    ))
                    : <p className="suggestion-empty">Подходящие профили не найдены</p>}
                </div>
              )}
            </div>
            <div className="filter-field procedure-field" ref={procedureFieldRef}>
              <label htmlFor="procedure-search">Методы лечения</label>
              <input
                id="procedure-search"
                type="search"
                value={procedureInput}
                onChange={(event) => setProcedureInput(event.target.value)}
                placeholder="Начните вводить процедуру"
                autoComplete="off"
              />
              {procedureInput.trim() && (suggestions.length > 0 || procedureError) && <div className="procedure-suggestions" role="listbox">
                {procedureError ? <p className="suggestion-error">{procedureError}</p> : suggestions.length
                  ? suggestions.map((procedure) => (
                    <button
                      type="button"
                      className="procedure-suggestion"
                      key={procedure.id || procedure.label}
                      onClick={() => chooseProcedure(procedure)}
                    >
                      <span>{procedure.label}</span><Icon name="next" size={15} />
                    </button>
                  ))
                  : <p className="suggestion-empty">Подходящие процедуры не найдены</p>}
              </div>}
            </div>
            <div className="filter-field region-filter" ref={regionFieldRef}>
              <label htmlFor="region-search">Регион</label>
              <input
                id="region-search"
                type="search"
                value={filters.region}
                onFocus={() => setShowRegionSuggestions(true)}
                onChange={(event) => {
                  updateFilter('region', event.target.value);
                  setShowRegionSuggestions(true);
                }}
                placeholder="Начните вводить регион"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showRegionSuggestions && matchingRegions.length > 0}
                aria-controls="region-suggestions"
              />
              {showRegionSuggestions && filters.region.trim() && (
                <div className="procedure-suggestions" id="region-suggestions" role="listbox">
                  {filterOptionsError
                    ? <p className="suggestion-error">Не удалось загрузить регионы: {filterOptionsError}</p>
                    : matchingRegions.length
                      ? matchingRegions.map((region) => (
                        <button
                          type="button"
                          className="procedure-suggestion"
                          key={region}
                          role="option"
                          aria-selected={filters.region === region}
                          onClick={() => {
                            updateFilter('region', region);
                            setShowRegionSuggestions(false);
                          }}
                        >
                          <span>{region}</span><Icon name="next" size={15} />
                        </button>
                      ))
                      : <p className="suggestion-empty">Подходящие регионы не найдены</p>}
                </div>
              )}
            </div>
            <label className="filter-field">
              <span>Тема экскурсий</span>
              <select
                value={filters.excursionTheme}
                onChange={(event) => updateFilter('excursionTheme', event.target.value)}
                aria-label="Фильтр по теме экскурсий"
              >
                <option value="">Любая тема</option>
                {excursionThemeOptions.map((theme) => <option key={theme} value={theme}>{theme}</option>)}
              </select>
              {filterOptionsError && <span className="filter-options-error">Не удалось загрузить темы экскурсий</span>}
            </label>
            <label className="filter-field">
              <span>Бассейн</span>
              <select value={filters.pool} onChange={(event) => updateFilter('pool', event.target.value)}>
                <option value="">Не важно</option>
                <option value="true">Есть бассейн</option>
                <option value="false">Без бассейна</option>
              </select>
            </label>
            <button
              className={`reset-button ${activeFilters ? 'visible' : ''}`}
              onClick={() => {
                setShowRegionSuggestions(false);
                setShowProfileSuggestions(false);
                resetFilters();
              }}
            >
              Сбросить
            </button>
          </div>
          {selectedProcedures.length > 0 && (
            <div className="selected-procedures-bar" aria-label="Выбранные методы лечения">
              <span>Выбраны методы лечения</span>
              <div className="selected-procedures">
                {selectedProcedures.map((procedure) => <button type="button" key={procedure} onClick={() => removeProcedure(procedure)}>{procedure}<Icon name="close" size={13} /></button>)}
              </div>
            </div>
          )}
        </div>

        {error ? (
          <div className="state-card error-state"><span className="state-symbol">!</span><h3>Не удалось загрузить санатории</h3><p>{error}</p><button className="outline-button" onClick={() => window.location.reload()}>Попробовать снова</button></div>
        ) : loading && catalogItems.length === 0 ? (
          <div className="catalog-grid" aria-label="Загрузка санаториев">
            {Array.from({ length: 6 }, (_, index) => <div className="skeleton-card" key={index}><div className="skeleton-photo" /><div className="skeleton-lines" /></div>)}
          </div>
        ) : catalogItems.length === 0 ? (
          <div className="state-card empty-state"><span className="state-symbol">⌕</span><h3>Пока ничего не нашлось</h3><p>Попробуйте изменить запрос или сбросить фильтры.</p><button className="outline-button" onClick={resetFilters}>Показать все санатории</button></div>
        ) : (
          <div className="catalog-grid">
            {catalogItems.map((item, index) => {
              const id = item.external_id || item._id;
              return (
                <SanatoriumCard
                  key={id}
                  data={item}
                  index={index}
                  onSelect={onSelect}
                  isFavorite={favoriteLinks.includes(sanatoriumPath(id))}
                  onToggleFavorite={onToggleFavorite}
                />
              );
            })}
          </div>
        )}

        {!error && <div className="infinite-scroll-sentinel" ref={sentinelRef} aria-hidden="true" />}
        {!error && loading && catalogItems.length > 0 && <p className="infinite-scroll-status">Загружаем следующие санатории…</p>}
        {!error && !loading && !hasMore && catalogItems.length > 0 && <p className="infinite-scroll-status">Вы просмотрели все санатории</p>}
      </section>
    </>
  );
}

function SanatoriumCard({ data, index, onSelect, isFavorite, onToggleFavorite }) {
  const name = getValue(data, 'general_info.name', 'Санаторий');
  const region = getValue(data, 'general_info.region');
  const address = getValue(data, 'general_info.address');
  const profiles = Array.isArray(data.medical_profile) ? data.medical_profile.slice(0, 2) : [];
  const photos = Array.isArray(data.photos) ? data.photos : [];
  const id = data.external_id || data._id;
  return (
    <article className={`sanatorium-card${data.infrastructure?.has_pool ? ' has-pool' : ''}`} style={{ '--card-index': index }}>
      <button className="card-image-button" onClick={() => onSelect(id)} aria-label={`Открыть ${name}`}>
        <div className="card-image" style={photos[0] ? { backgroundImage: `url("${photos[0]}")` } : undefined}>
          <span className="card-location"><Icon name="pin" size={14} /> {region || address || 'Россия'}</span>
          {data.infrastructure?.has_pool && <span className="pool-badge"><Icon name="pool" size={15} /> Бассейн</span>}
        </div>
      </button>
      <button
        type="button"
        className={`card-favorite${isFavorite ? ' active' : ''}`}
        onClick={() => onToggleFavorite(id)}
        aria-label={isFavorite ? `Убрать ${name} из избранного` : `Добавить ${name} в избранное`}
        aria-pressed={isFavorite}
        title={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
      >
        <Icon name={isFavorite ? 'heart-filled' : 'heart'} size={19} />
      </button>
      <div className="card-content">
        <div className="card-meta"><span>{getValue(data, 'resort_info.resort_type', 'Санаторно-курортный отдых')}</span><span className="card-dot">·</span><span>{region || 'Россия'}</span></div>
        <button className="card-title" onClick={() => onSelect(id)}>{name}</button>
        {address && <p className="card-address">{address}</p>}
        <div className="profile-tags">
          {profiles.map((profile) => <span key={profile}>{profile}</span>)}
          {data.medical_profile?.length > profiles.length && <span>+{data.medical_profile.length - profiles.length}</span>}
          {profiles.length === 0 && <span>Оздоровительный отдых</span>}
        </div>
        <div className="card-bottom">
          <div className="card-price">{formatPrice(getValue(data, 'pricing.min_price_per_night', 0))}<small> / ночь</small></div>
          <button className="card-arrow" onClick={() => onSelect(id)} aria-label={`Подробнее: ${name}`}><Icon name="arrow" size={18} /></button>
        </div>
      </div>
    </article>
  );
}

function PhotoCarousel({ photos, region }) {
  const [activePhoto, setActivePhoto] = useState(0);
  useEffect(() => setActivePhoto(0), [photos]);
  const photo = photos[activePhoto];
  const source = typeof photo === 'string' ? photo : photo?.url || photo?.src || '';

  return (
    <div className={`detail-cover ${source ? '' : 'detail-cover-empty'}`}>
      {source && <img className="detail-photo" src={source} alt={`${region || 'Санаторий'} — фото ${activePhoto + 1}`} />}
      <div className="detail-cover-caption">{region || 'ОТДЫХ И ВОССТАНОВЛЕНИЕ'}</div>
      {photos.length > 1 && <>
        <button className="photo-arrow photo-arrow-prev" type="button" onClick={() => setActivePhoto((index) => (index - 1 + photos.length) % photos.length)} aria-label="Предыдущее фото"><Icon name="back" size={19} /></button>
        <button className="photo-arrow photo-arrow-next" type="button" onClick={() => setActivePhoto((index) => (index + 1) % photos.length)} aria-label="Следующее фото"><Icon name="next" size={19} /></button>
        <span className="photo-count">{activePhoto + 1} / {photos.length}</span>
        <div className="photo-dots">{photos.map((item, index) => <button key={typeof item === 'string' ? item : item?.url || index} type="button" className={index === activePhoto ? 'photo-dot active' : 'photo-dot'} onClick={() => setActivePhoto(index)} aria-label={`Фото ${index + 1}`} />)}</div>
      </>}
    </div>
  );
}

function SanatoriumMap({ address, coordinates, name }) {
  const mapRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const hasCoordinates = coordinates?.lat !== null
    && coordinates?.lat !== undefined
    && coordinates?.lat !== ''
    && coordinates?.lon !== null
    && coordinates?.lon !== undefined
    && coordinates?.lon !== ''
    && Number.isFinite(Number(coordinates.lat))
    && Number.isFinite(Number(coordinates.lon));
  const fallbackMapUrl = useMemo(() => {
    const params = new URLSearchParams({
      text: address || name,
      z: '15',
      lang: 'ru_RU',
    });
    if (hasCoordinates) {
      const point = `${Number(coordinates.lon)},${Number(coordinates.lat)}`;
      params.set('ll', point);
      params.set('pt', `${point},pm2rdm`);
    }
    return `https://yandex.ru/map-widget/v1/?${params.toString()}`;
  }, [address, coordinates?.lat, coordinates?.lon, hasCoordinates, name]);

  useEffect(() => {
    if (!address && !hasCoordinates) return undefined;
    let cancelled = false;
    let map;
    setStatus('loading');

    loadYandexMaps()
      .then(async (ymaps) => {
        let center;
        if (address) {
          const result = await ymaps.geocode(address, { results: 1 });
          const firstResult = result.geoObjects.get(0);
          if (!firstResult) throw new Error('Яндекс Карты не нашли санаторий по этому адресу.');
          center = firstResult.geometry.getCoordinates();
        } else {
          center = [Number(coordinates.lat), Number(coordinates.lon)];
        }
        if (cancelled || !mapRef.current) return;

        map = new ymaps.Map(mapRef.current, {
          center,
          zoom: 15,
          controls: ['zoomControl'],
        });
        map.geoObjects.add(new ymaps.Placemark(center, {
          balloonContentHeader: name,
          balloonContentBody: address,
          hintContent: name,
        }, {
          preset: 'islands#greenDotIcon',
        }));
        setStatus('ready');
      })
      .catch((error) => {
        if (!cancelled) {
          if (map) {
            map.destroy();
            map = null;
          }
          console.error('Yandex Maps API failed; displaying the embedded map instead.', error);
          setStatus('fallback');
        }
      });

    return () => {
      cancelled = true;
      if (map) map.destroy();
    };
  }, [address, coordinates?.lat, coordinates?.lon, hasCoordinates, name]);

  if (!address && !hasCoordinates) return null;
  return (
    <section className="detail-section map-section">
      <span className="eyebrow">РАСПОЛОЖЕНИЕ</span>
      <h2>Как нас найти</h2>
      {address && <p className="map-address">{address}</p>}
      {status === 'fallback'
        ? <iframe className="map-canvas map-fallback" title={`Карта расположения: ${name}`} src={fallbackMapUrl} loading="lazy" referrerPolicy="strict-origin-when-cross-origin" />
        : <div className="map-canvas" ref={mapRef} role="application" aria-label={`Карта расположения: ${name}`} />}
      {status === 'loading' && <p className="map-status">Ищем адрес на карте…</p>}
    </section>
  );
}

function DetailPage({ data, loading, error, onBack, sanatoriumId, isFavorite, onToggleFavorite }) {
  if (loading) return <div className="detail-state"><div className="detail-skeleton" /><div className="detail-skeleton short" /><span>Загружаем информацию о санатории…</span></div>;
  if (error) return <div className="detail-state error-state"><span className="state-symbol">!</span><h3>Не удалось загрузить санаторий</h3><p>{error}</p><button className="outline-button" onClick={onBack}>Вернуться в каталог</button></div>;
  if (!data) return null;

  const name = getValue(data, 'general_info.name', 'Санаторий');
  const photos = Array.isArray(data.photos) ? data.photos : [];
  const profiles = Array.isArray(data.medical_profile) ? data.medical_profile : [];
  const procedures = Array.isArray(data.treatments_and_procedures) ? data.treatments_and_procedures : [];
  const themes = Array.isArray(data.excursionThemes) ? data.excursionThemes : [];
  const address = getValue(data, 'general_info.address');
  const website = getValue(data, 'general_info.official_website');
  const anotherExcursion = getValue(data, 'another_excursion');

  return (
    <article className="detail-page">
      <div className="detail-topline">
        <button className="back-link" onClick={onBack}><Icon name="back" size={17} /> Все санатории</button>
        <span className="detail-breadcrumb">Россия <span>/</span> {getValue(data, 'general_info.region', 'Санаторий')}</span>
      </div>
      <div className="detail-hero">
        <PhotoCarousel photos={photos} region={getValue(data, 'general_info.region')} />
        <div className="detail-intro">
          <span className="eyebrow">{getValue(data, 'resort_info.resort_type', 'САНАТОРИЙ')}</span>
          <h1>{name}</h1>
          {address && <p className="detail-address"><Icon name="pin" size={17} /> {address}</p>}
          <p className="detail-price">{formatPrice(getValue(data, 'pricing.min_price_per_night', 0))}<span> / ночь</span></p>
          <div className="detail-quick-facts">
            <div><span>Заезд</span><strong>{formatTime(getValue(data, 'pricing.check_in_time'))}</strong></div>
            <div><span>Выезд</span><strong>{formatTime(getValue(data, 'pricing.check_out_time'))}</strong></div>
            <div><span>Бассейн</span><strong>{getValue(data, 'infrastructure.has_pool', false) ? 'Есть' : 'Нет данных'}</strong></div>
          </div>
          <div className="detail-actions">
            {website
              ? <a className="primary-button" href={website} target="_blank" rel="noreferrer">Официальный сайт <Icon name="arrow" size={17} /></a>
              : <span className="primary-button disabled-link">Контакты санатория</span>}
            <button
              type="button"
              className={`favorite-detail-button${isFavorite ? ' active' : ''}`}
              onClick={() => onToggleFavorite(sanatoriumId)}
              aria-pressed={isFavorite}
            >
              <Icon name={isFavorite ? 'heart-filled' : 'heart'} size={18} />
              {isFavorite ? 'В избранном' : 'Добавить в избранное'}
            </button>
          </div>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-main-column">
          <section className="detail-section description-section">
            <span className="eyebrow">О МЕСТЕ</span>
            <h2>Отдых с заботой о себе</h2>
            <MarkdownText>{getValue(data, 'ai_generated_description', 'Подробное описание пока не добавлено.')}</MarkdownText>
            {getValue(data, 'general_info.full_name') && <p className="full-name">{getValue(data, 'general_info.full_name')}</p>}
          </section>
          <section className="detail-section">
            <span className="eyebrow">ЗДОРОВЬЕ И ВОССТАНОВЛЕНИЕ</span>
            <h2>Медицинские профили</h2>
            {profiles.length ? <div className="detail-tags">{profiles.map((profile) => <span key={profile}>{profile}</span>)}</div> : <p className="muted-copy">Информация о медицинских профилях уточняется.</p>}
            {procedures.length > 0 && <div className="procedure-list">
              {procedures.map((group, index) => (
                <div className="procedure-group" key={`${group.category || 'procedure'}-${index}`}>
                  <h3>{group.category || 'Процедуры и методы лечения'}</h3>
                  {Array.isArray(group.procedures) && <ul>{group.procedures.map((procedure, procedureIndex) => <li key={`${procedure}-${procedureIndex}`}>{procedure}</li>)}</ul>}
                </div>
              ))}
            </div>}
          </section>
          <section className="detail-section">
            <span className="eyebrow">ИНФРАСТРУКТУРА</span>
            <h2>Комфорт во время отдыха</h2>
            <div className="info-grid">
              <InfoItem label="Питание" value={getValue(data, 'infrastructure.meal_type', 'Информация не указана')} />
              <InfoItem label="Период работы" value={getValue(data, 'characteristics.operating_period', 'Информация не указана')} />
              <InfoItem label="Для кого" value={getValue(data, 'characteristics.target_audience', 'Для всех')} />
              <InfoItem label="Тип курорта" value={getValue(data, 'resort_info.resort_type', 'Информация не указана')} />
            </div>
            {getValue(data, 'infrastructure.services_description') && <p className="services-copy">{getValue(data, 'infrastructure.services_description')}</p>}
          </section>
          <SanatoriumMap
            address={address}
            coordinates={getValue(data, 'accessibility.coordinates', {})}
            name={name}
          />
          <section className="detail-section excursion-section">
            <span className="eyebrow">РЯДОМ</span>
            <h2>Чем заняться поблизости</h2>
            <p className="excursion-copy">С экскурсионными программами санатория можете ознакомиться на официальном сайте.</p>
            {website
              ? <a className="excursion-button" href={website} target="_blank" rel="noreferrer">Перейти <Icon name="arrow" size={16} /></a>
              : <span className="excursion-unavailable">Официальный сайт не указан</span>}
            {anotherExcursion && <a className="other-excursions" href={anotherExcursion} target="_blank" rel="noreferrer">Другие экскурсии</a>}
            {themes.length > 0 && <div className="detail-tags">{themes.map((theme, index) => <span key={`${theme}-${index}`}>{typeof theme === 'string' ? theme : JSON.stringify(theme)}</span>)}</div>}
          </section>
        </div>
        <aside className="contact-card">
          <span className="eyebrow">ПОЛЕЗНО ЗНАТЬ</span>
          <h3>Контакты и дорога</h3>
          <ContactItem label="Телефон" value={getValue(data, 'general_info.phone')} href={getValue(data, 'general_info.phone') ? `tel:${getValue(data, 'general_info.phone')}` : ''} />
          <ContactItem label="Электронная почта" value={getValue(data, 'general_info.email')} href={getValue(data, 'general_info.email') ? `mailto:${getValue(data, 'general_info.email')}` : ''} />
          {getValue(data, 'accessibility.how_to_get') && <div className="contact-item"><span>Как добраться</span><p>{getValue(data, 'accessibility.how_to_get')}</p></div>}
          {website && <a className="contact-site" href={website} target="_blank" rel="noreferrer">{website.replace(/^https?:\/\//, '')} <Icon name="arrow" size={15} /></a>}
        </aside>
      </div>
    </article>
  );
}

function InfoItem({ label, value }) {
  return <div className="info-item"><span>{label}</span><strong>{String(value)}</strong></div>;
}

function ContactItem({ label, value, href }) {
  if (!value) return null;
  return <div className="contact-item"><span>{label}</span><a href={href}>{value}</a></div>;
}

function FavoritesWidget({ open, setOpen, links, onRemove, onSelect, titles }) {
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open, setOpen]);

  if (!open) {
    return (
      <button className="favorites-launcher" type="button" onClick={() => setOpen(true)} aria-label={`Открыть избранное, ${links.length} санаториев`}>
        <Icon name="heart" size={21} />
        <span>Избранное</span>
        {links.length > 0 && <span className="favorites-count">{links.length}</span>}
      </button>
    );
  }

  return (
    <>
      <button className="favorites-backdrop" type="button" onClick={() => setOpen(false)} aria-label="Закрыть окно избранного" />
      <section className="favorites-widget" role="dialog" aria-modal="true" aria-label="Избранные санатории">
      <header className="favorites-header">
        <div className="favorites-heading">
          <Icon name="heart-filled" size={19} />
          <div><strong>Избранное</strong><span>{links.length} {pluralize(links.length, 'санаторий', 'санатория', 'санаториев')}</span></div>
        </div>
        <button className="favorites-close" type="button" onClick={() => setOpen(false)} aria-label="Закрыть избранное"><Icon name="close" size={19} /></button>
      </header>
      {links.length
        ? <ul className="favorites-list">
          {links.map((link) => {
            const id = sanatoriumIdFromPath(link);
            const title = titles[link] || `Санаторий №${id}`;
            return (
              <li className="favorite-item" key={link}>
                <a
                  href={link}
                  onClick={(event) => {
                    event.preventDefault();
                    if (id) onSelect(id);
                  }}
                  title={title}
                >
                  <span>{title}</span><Icon name="arrow" size={16} />
                </a>
                <button type="button" onClick={() => onRemove(link)} aria-label={`Убрать ${title} из избранного`} title="Убрать из избранного">
                  <Icon name="close" size={16} />
                </button>
              </li>
            );
          })}
        </ul>
        : <div className="favorites-empty">
          <Icon name="heart" size={28} />
          <p>Здесь будут санатории, которые вы добавите в избранное.</p>
        </div>}
      </section>
    </>
  );
}

function ChatMessage({ message, onSelectSanatorium }) {
  const isUser = message.sender === 'user';
  return (
    <div className={`message-row ${isUser ? 'user-row' : ''}`}>
      <div className={isUser ? 'user-message' : 'assistant-message'}>
        {isUser ? message.text : <MarkdownText>{assistantAnswer(message.text)}</MarkdownText>}
        {message.sanatorium_id && <button className="chat-result-link" onClick={() => onSelectSanatorium(message.sanatorium_id)}>Посмотреть санаторий <Icon name="arrow" size={14} /></button>}
      </div>
    </div>
  );
}

function ChatWidget({ open, setOpen, onSelectSanatorium }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const lastMessageIdRef = useRef('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    let initialized = false;

    const pollForMessages = async () => {
      while (!controller.signal.aborted) {
        try {
          const lastMessageId = lastMessageIdRef.current;
          const data = await request('/api/chat/poll' + (lastMessageId ? `?last_message_id=${encodeURIComponent(lastMessageId)}` : ''), { signal: controller.signal });
          const incoming = Array.isArray(data.messages) ? data.messages : [];
          if (incoming.length) {
            lastMessageIdRef.current = incoming[incoming.length - 1].id || lastMessageId;
            setMessages((current) => {
              const knownIds = new Set(current.map((message) => message.id));
              return [...current, ...incoming.filter((message) => !knownIds.has(message.id))];
            });
          }
          if (!initialized) {
            initialized = true;
            setLoading(false);
          }
          setError('');
        } catch (pollError) {
          if (pollError.name === 'AbortError') return;
          setError(pollError.message);
          await new Promise((resolve) => window.setTimeout(resolve, 1500));
        }
      }
    };

    setLoading(true);
    request('/api/user/me', { signal: controller.signal })
      .then((user) => {
        const history = user?.chat?.messages || [];
        setMessages(history);
        lastMessageIdRef.current = history.length ? history[history.length - 1].id : '';
      })
      .then(pollForMessages)
      .catch((initialError) => {
        if (initialError.name !== 'AbortError') {
          setError(initialError.message);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading, sending]);

  const sendMessage = async (event) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    const temporaryId = `pending-${Date.now()}`;
    const optimisticMessage = { id: temporaryId, sender: 'user', text, created_at: new Date().toISOString() };
    setMessages((current) => [...current, optimisticMessage]);
    setInput('');
    setSending(true);
    setError('');
    try {
      const result = await request('/api/chat/send', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      lastMessageIdRef.current = result.message.id;
      setMessages((current) => {
        if (current.some((message) => message.id === result.message.id)) {
          return current.filter((message) => message.id !== temporaryId);
        }
        return current.map((message) => message.id === temporaryId ? result.message : message);
      });
    } catch (sendError) {
      setMessages((current) => current.filter((message) => message.id !== temporaryId));
      setInput(text);
      setError(sendError.message);
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return <button className="chat-launcher" onClick={() => setOpen(true)} aria-label="Открыть AI-чат">
      <Icon name="chat" size={22} /><span>Помочь с выбором</span><span className="chat-launcher-spark">✳</span>
    </button>;
  }

  return (
    <section className="chat-widget" aria-label="AI-чат">
      <header className="chat-header">
        <span className="chat-avatar">с<span>✳</span></span>
        <div className="chat-heading"><strong>Ваш AI-помощник</strong><span><i /> На связи, чтобы помочь</span></div>
        <button className="chat-close" onClick={() => setOpen(false)} aria-label="Свернуть чат"><Icon name="close" size={19} /></button>
      </header>
      <div className="chat-messages" aria-live="polite">
        <div className="chat-date">ПОДБОР САНАТОРИЯ</div>
        {messages.length === 0 && !loading && <div className="assistant-message">Здравствуйте! Расскажите, какой отдых вы ищете — помогу подобрать санаторий. Например, можно указать регион, бюджет или профиль лечения.</div>}
        {messages.map((message) => <ChatMessage key={message.id} message={message} onSelectSanatorium={(id) => { setOpen(false); onSelectSanatorium(id); }} />)}
        {(loading || sending) && <div className="assistant-message typing"><span /><span /><span /></div>}
        {error && <p className="chat-error">{error}</p>}
        <div ref={chatEndRef} />
      </div>
      <form className="chat-input-row" onSubmit={sendMessage}>
        <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="Напишите, что вы ищете…" aria-label="Сообщение AI-помощнику" />
        <button type="submit" disabled={!input.trim() || sending} aria-label="Отправить сообщение"><Icon name="send" size={17} /></button>
      </form>
      <div className="chat-disclaimer">Ответы AI могут быть неточными. Уточняйте детали у санатория.</div>
    </section>
  );
}

function pluralize(number, one, few, many) {
  const remainder10 = number % 10;
  const remainder100 = number % 100;
  if (remainder10 === 1 && remainder100 !== 11) return one;
  if (remainder10 >= 2 && remainder10 <= 4 && (remainder100 < 12 || remainder100 > 14)) return few;
  return many;
}

export default App;
